import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetService, userGroupService } from '../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Avatar,
  IconButton,
  Tooltip,
  FormHelperText,
  Chip,
  LinearProgress,

  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Key as KeyIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';

// Importar constantes e validações compartilhadas
import {
  ASSET_CATEGORIES,
  ASSET_STATUSES,
  getCategoryInfo,
  getStatusInfo,
  validateAssetData,
  validateTokenAssetData,
  sanitizeAssetData
} from '../constants/assetConstants';

const TokenAssetForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: '',
    category: 'token',
    description: '',
    owner: '',
    location: '',
    status: 'active',
    value: '',
    permission_group_ids: [],
    metadata: {},
    // Campo específico para tokens
    token_value: '',
    token_service: '',
    token_environment: '',
    token_related_url: '',
    token_auth_type: '',
    token_scopes: '',
    token_project: '',
    token_technical_owner: '',
    expiry_date: ''
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isEdit, setIsEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (id) {
      loadAsset(id);
    }
  }, [id]);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await userGroupService.getUserGroups();
      setGroups(response.groups || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const loadAsset = async (assetId) => {
    try {
      setLoading(true);
      const response = await assetService.getAsset(assetId, 'token');
      const asset = response.asset;

      setFormData({
        name: asset.name || '',
        category: 'token',
        description: asset.description || '',
        owner: asset.owner || '',
        location: asset.location || '',
        status: asset.status || 'active',
        value: asset.value || '',
        permission_group_ids: asset.permission_group_ids || (asset.group_id ? [asset.group_id] : []),
        metadata: asset.asset_metadata || {},
        // Campo específico para tokens
        token_value: asset.token_value || '',
        token_service: asset.token_service || '',
        token_environment: asset.token_environment || '',
        token_related_url: asset.token_related_url || '',
        token_auth_type: asset.token_auth_type || '',
        token_scopes: asset.token_scopes || '',
        token_project: asset.token_project || '',
        token_technical_owner: asset.token_technical_owner || '',
        expiry_date: asset.expiry_date || ''
      });

      setIsEdit(true);
    } catch (error) {
      console.error('Erro ao carregar ativo:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar ativo', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Validação em tempo real
  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    // Validações básicas
    if (name === 'name' && (!value || value.trim().length < 3)) {
      errors.name = 'Nome deve ter pelo menos 3 caracteres';
    } else if (name === 'name') {
      delete errors.name;
    }

    if (name === 'value' && value && isNaN(parseFloat(value))) {
      errors.value = 'Valor deve ser um número válido';
    } else if (name === 'value') {
      delete errors.value;
    }

    // Validação específica para token
    if (name === 'token_value' && (!value || value.trim().length < 10)) {
      errors.token_value = 'Token deve ter pelo menos 10 caracteres';
    } else if (name === 'token_value') {
      delete errors.token_value;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handler com validação
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));

    // Validar campo em tempo real
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar campos obrigatórios
    if (!formData.name || formData.name.trim().length < 3) {
      setValidationErrors(prev => ({ ...prev, name: 'Nome deve ter pelo menos 3 caracteres' }));
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    if (!formData.token_value || formData.token_value.trim().length < 10) {
      setValidationErrors(prev => ({ ...prev, token_value: 'Token deve ter pelo menos 10 caracteres' }));
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        category: 'token',
        description: formData.description,
        owner: formData.owner,
        location: formData.location,
        status: formData.status,
        value: formData.value ? parseFloat(formData.value) : null,
        permission_group_ids: formData.permission_group_ids || [],
        metadata: { ...(formData.metadata || {}) },
        // Campo específico para tokens
        token_value: formData.token_value,
        token_service: formData.token_service,
        token_environment: formData.token_environment,
        token_related_url: formData.token_related_url,
        token_auth_type: formData.token_auth_type,
        token_scopes: formData.token_scopes,
        token_project: formData.token_project,
        token_technical_owner: formData.token_technical_owner,
        expiry_date: formData.expiry_date || null
      };

      if (isEdit && id) {
        await assetService.updateAsset(id, submitData);
        setSnackbar({ open: true, message: 'Token atualizado com sucesso!', severity: 'success' });
      } else {
        await assetService.createAsset(submitData);
        setSnackbar({ open: true, message: 'Token criado com sucesso!', severity: 'success' });
      }

      setTimeout(() => navigate('/assets/category/token'), 1500);
    } catch (error) {
      console.error('Erro no formulário:', error);
      setSnackbar({
        open: true,
        message: error.message || (isEdit ? 'Erro ao atualizar token' : 'Erro ao criar token'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const currentCategory = ASSET_CATEGORIES['token'];

  // Loading state
  if (loading && !isEdit) {
    return (
      <Layout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <LinearProgress sx={{ mb: 3 }} />
          <Typography variant="h6">Salvando token...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>Voltar</Button>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              <KeyIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">
                {isEdit ? 'Editar Token' : 'Novo Token'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentCategory?.description || 'Token de acesso'}
              </Typography>
            </Box>
          </Box>

          {/* Indicador de progresso */}
          {Object.keys(validationErrors).length > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${Object.keys(validationErrors).length} erro(s) de validação`}
              color="warning"
              variant="outlined"
            />
          )}
        </Box>



        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Informações Básicas - Step 1 */}
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                      <InfoIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Informações Básicas</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nome do Token"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        error={!!validationErrors.name}
                        helperText={validationErrors.name}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled>
                        <InputLabel>Categoria</InputLabel>
                        <Select value="token" label="Categoria">
                          <MenuItem value="token">Tokens</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Status</InputLabel>
                        <Select name="status" value={formData.status} onChange={handleChange} label="Status">
                          {Object.entries(ASSET_STATUSES).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                              <Chip label={config.label} color={config.color} size="small" sx={{ mr: 1 }} />
                              {config.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Descrição"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Descreva o propósito deste token..."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 32, height: 32 }}>
                      <InfoIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Contexto de API</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Serviço"
                        name="token_service"
                        value={formData.token_service}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: OpenAI, AWS, HubSpot"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Ambiente</InputLabel>
                        <Select
                          name="token_environment"
                          value={formData.token_environment}
                          onChange={handleChange}
                          label="Ambiente"
                        >
                          <MenuItem value="">Não informado</MenuItem>
                          <MenuItem value="Produção">Produção</MenuItem>
                          <MenuItem value="Homologação">Homologação</MenuItem>
                          <MenuItem value="Desenvolvimento">Desenvolvimento</MenuItem>
                          <MenuItem value="Teste">Teste</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Endpoint/URL relacionado"
                        name="token_related_url"
                        value={formData.token_related_url}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="https://api.exemplo.com"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Tipo de autenticação</InputLabel>
                        <Select
                          name="token_auth_type"
                          value={formData.token_auth_type}
                          onChange={handleChange}
                          label="Tipo de autenticação"
                        >
                          <MenuItem value="">Não informado</MenuItem>
                          <MenuItem value="Bearer">Bearer</MenuItem>
                          <MenuItem value="API Key">API Key</MenuItem>
                          <MenuItem value="OAuth">OAuth</MenuItem>
                          <MenuItem value="Basic">Basic</MenuItem>
                          <MenuItem value="Outro">Outro</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Projeto"
                        name="token_project"
                        value={formData.token_project}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Responsável técnico"
                        name="token_technical_owner"
                        value={formData.token_technical_owner}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Validade"
                        name="expiry_date"
                        type="date"
                        value={formData.expiry_date}
                        onChange={handleChange}
                        disabled={loading}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Escopos/Permissões"
                        name="token_scopes"
                        value={formData.token_scopes}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: read:users, write:reports"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Token - Step 2 */}
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 32, height: 32 }}>
                      <KeyIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Token de Acesso</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Valor do Token *"
                        name="token_value"
                        value={formData.token_value}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        error={!!validationErrors.token_value}
                        helperText={validationErrors.token_value || 'Cole aqui o token de acesso completo'}
                        placeholder="sk-... ou qualquer formato de token"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Informações Adicionais - Step 3 */}
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 32, height: 32 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Informações Adicionais</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Valor (R$)"
                        name="value"
                        type="number"
                        step="0.01"
                        value={formData.value}
                        onChange={handleChange}
                        disabled={loading}
                        error={!!validationErrors.value}
                        helperText={validationErrors.value}
                        InputProps={{
                          startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Localização/Sistema"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        disabled={loading}
                        InputProps={{
                          startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        placeholder="Ex: AWS, Azure, Sistema Interno..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Responsável"
                        name="owner"
                        value={formData.owner}
                        onChange={handleChange}
                        disabled={loading}
                        InputProps={{
                          startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Grupos de Permissão</InputLabel>
                        <Select
                          name="permission_group_ids"
                          value={formData.permission_group_ids}
                          onChange={handleChange}
                          label="Grupos de Permissão"
                          multiple
                        >
                          {groups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Card de Ações e Validação */}
              <Card elevation={2} sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Ações</Typography>

                  {/* Resumo de validação */}
                  <Box sx={{ mb: 2 }}>
                    {Object.keys(validationErrors).length === 0 ? (
                      <Alert severity="success" icon={<CheckCircleIcon />}>
                        Todos os campos obrigatórios foram preenchidos!
                      </Alert>
                    ) : (
                      <Alert severity="warning" icon={<WarningIcon />}>
                        {Object.keys(validationErrors).length} campo(s) precisam de atenção
                      </Alert>
                    )}
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<SaveIcon />}
                    disabled={loading || Object.keys(validationErrors).length > 0}
                  >
                    {loading ? 'Salvando...' : 'Salvar Token'}
                  </Button>

                  {Object.keys(validationErrors).length > 0 && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      Corrija os erros acima antes de salvar
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>

        {/* Snackbar melhorado */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default TokenAssetForm;
